import { CaretLeftIcon, CheckCircleIcon, CheckIcon } from '@phosphor-icons/react'
import React, { useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useCreateMarketShopMobilePayment } from '@/api/market-shop/checkout/create-market-shop-mobile-payment'
import { useGetCalculatedMarketShopCart } from '@/api/market-shop/checkout/get-calculated-market-shop-cart'
import { useGetSettingsByToken } from '@/api/market-shop/client/get-settings-by-token'
import { Button } from '@/components/business/market-shop/button'
import { CheckoutPaymentMethod } from '@/components/business/market-shop/checkout-payment-method'
import { OrderReceive } from '@/components/business/market-shop/order-receive'
import { OrderStatusBar } from '@/components/business/market-shop/order-status-bar'
import { OrderSummary } from '@/components/business/market-shop/order-summary'
import { PopupWindow } from '@/components/business/market-shop/popup-window'
import { Tips } from '@/components/business/market-shop/tips'
import { Alert } from '@/components/ui/alert'
import { Icon } from '@/components/ui/icon'
import { Typography } from '@/components/ui/typography'
import { ELEVATEOS_LOGO_ALT } from '@/constants'
import { PopupRequiredProps, popupFactory } from '@/factories'
import { MarketShopDeliveryType, MarketShopMobilePaymentMethod, NullableType } from '@/models'
import { ContactlessPaymentModal } from '@/pages/market-shop/contactless-payment-modal'
import { MARKET_SHOP_CURRENCY } from '@/pages/market-shop/market-shop.constants'
import { formatPrice, transformDollarsToCents } from '@/utils'
import { getApiErrorMessage } from '@/utils/error'

import { useMarketShopStore } from '../market-shop.store'
import * as S from './check-out.styled'
import { StripePayment, StripePaymentInfo } from './stripe-payment'

export type CheckOutProps = PopupRequiredProps

export const CheckOut = ({ onClose }: CheckOutProps) => {
  const { t } = useTranslation(['market-shop'])
  const marketShopStore = useMarketShopStore()
  const hasProductsInCart = marketShopStore.hasProductsInCart()

  const {
    subTotalPrice: cartSubTotalPrice,
    taxPrice: cartTaxPrice,
    totalPrice: cartTotalPrice,
    isFetching: isCartFetching,
    discount: cartDiscount,
  } = useGetCalculatedMarketShopCart(marketShopStore.getMappedCartRequest())

  const { data: settings, isResidentFlow } = useGetSettingsByToken()

  const { mutateAsync: createMobilePayment } = useCreateMarketShopMobilePayment()

  const logoRef = useRef<HTMLImageElement>(null)
  const stripePaymentRef = useRef<StripePaymentInfo>(null)

  const [transactionError, setTransactionError] = useState('')
  const [transactionId, setTransactionId] = useState('')

  const [processingPayment, setProcessingPayment] = useState(false)
  const [deliveryType, setDeliveryType] = useState<MarketShopDeliveryType>(() => {
    // NOTE: if `deliverToMe` is disabled in settings ->
    // set `pickup` as initial and the only available option
    return settings?.deliverToMe ? 'delivery' : 'pickup'
  })
  const [checkoutPaymentMethod, setCheckoutPaymentMethod] =
    useState<MarketShopMobilePaymentMethod | null>(() => {
      // NOTE: If more than 1 option allowed -> select null to force user pick one.
      // Select the appropriate option otherwise
      if (settings?.enablePayByCash && settings?.enablePayByCard) return null
      if (settings?.enablePayByCash) return 'cash'
      if (settings?.enablePayByCard) return 'card'
      return null
    })
  const [prepopulatedTipValue, setPrepopulatedTipValue] = useState<NullableType<number>>(0)
  const [customTipsAmount, setCustomTipsAmount] = useState('')

  const tipsAmountInCents = useMemo(() => {
    return customTipsAmount
      ? transformDollarsToCents(customTipsAmount)
      : (prepopulatedTipValue ?? 0)
  }, [prepopulatedTipValue, customTipsAmount])

  const subTotalPrice = cartSubTotalPrice + tipsAmountInCents
  const taxPrice = cartTaxPrice
  const totalPrice = cartTotalPrice + tipsAmountInCents
  const discount = cartDiscount

  const isCardPayment = checkoutPaymentMethod === 'card'
  const hasToPayNow = totalPrice && isCardPayment

  const handlePaymentError = (message?: string) => {
    // NOTE: when payment is unsuccessful -> scroll to the top of the page (till logo),
    // so, user will actually be able to see an alert message
    logoRef.current?.scrollIntoView({ behavior: 'smooth' })
    setTransactionError(message || t('market-shop:checkOut.genericPaymentError'))
  }

  const handlePaymentSuccess = (newTransactionId: string) => {
    // NOTE: when payment is successful -> scroll to the top of the page (till logo),
    // so, user will actually be able to see successful payment information
    logoRef.current?.scrollIntoView({ behavior: 'smooth' })
    setTransactionId(newTransactionId)
  }

  const onSubmit = async () => {
    if (!checkoutPaymentMethod) {
      handlePaymentError(t('market-shop:checkOut.choosePaymentMethodError'))
      return
    }

    try {
      setTransactionError('')
      setProcessingPayment(true)

      const currentSPI = stripePaymentRef.current
      if (currentSPI?.waitUntilReady) {
        await currentSPI.waitUntilReady()
      }

      const stripe = currentSPI?.stripe
      const elements = currentSPI?.elements ?? undefined

      if (elements) {
        const { error: elementsError } = await elements.submit()
        if (elementsError) {
          setProcessingPayment(false)
          // NOTE: when stripe validation unsuccessful -> scroll to its container
          // so, user will actually be able to see what's wrong with the payment form
          currentSPI?.container?.scrollIntoView({ behavior: 'smooth' })
          return
        }
      }

      const { clientSecret, orderId } = await createMobilePayment({
        deliveryType,
        orderNotes: marketShopStore.note,
        paymentMethod: checkoutPaymentMethod,
        sessionId: marketShopStore.sessionId,
        tipAmount: tipsAmountInCents || undefined,
      })

      if (clientSecret) {
        if (!stripe) {
          throw new Error(t('market-shop:checkOut.stripeNotInitializedError'))
        }

        const { error } = await stripe.confirmPayment({
          clientSecret,
          confirmParams: {
            return_url: window.location.href,
          },
          elements,
          redirect: 'if_required',
        })
        if (error) throw error
      }

      setProcessingPayment(false)
      handlePaymentSuccess(orderId.toString())
    } catch (error) {
      setProcessingPayment(false)
      handlePaymentError(getApiErrorMessage(error))
    }
  }

  const handlePayNowClick = () => {
    if (isResidentFlow) {
      void onSubmit()
      return
    }

    setProcessingPayment(true)
    popupFactory.open(
      ContactlessPaymentModal,
      {
        onClose: () => setProcessingPayment(false),
        onSuccessPayment: handlePaymentSuccess,
      },
      { suspendCurrent: true },
    )
  }

  const handlePrepopulatedTipClick = (newTip: number) => {
    setPrepopulatedTipValue(newTip)
    setCustomTipsAmount('')
  }

  const handleCustomTipAmountChange = (newValue: string) => {
    setPrepopulatedTipValue(newValue ? null : 0)
    setCustomTipsAmount(newValue)
  }

  const handlePaymentMethodChange = (value: MarketShopMobilePaymentMethod) => {
    setCheckoutPaymentMethod(value)
    setPrepopulatedTipValue(0)
    setCustomTipsAmount('')
  }

  const handleSuccessfulTransactionCloseClick = () => {
    marketShopStore.reset()
  }

  return (
    <PopupWindow onClose={onClose}>
      {() => (
        <>
          <S.Content>
            <S.Logo
              ref={logoRef}
              src="/images/logo/elevateos-logo-wide-black.svg"
              alt={ELEVATEOS_LOGO_ALT}
            />
            <S.Modules>
              {transactionError && <Alert type="error" title={transactionError} />}
              {transactionId && (
                <S.SuccessfulPaymentContainer>
                  <Icon icon={CheckCircleIcon} size="4rem" />
                  <Typography as="h2" size="heading-large" weight="semibold">
                    {t('market-shop:checkOut.paymentSuccessTitle')}
                  </Typography>
                  <Typography as="span" size="text-medium">
                    {t('market-shop:checkOut.orderNumber', { id: transactionId })}
                  </Typography>
                </S.SuccessfulPaymentContainer>
              )}
              {transactionId && isResidentFlow && (
                <OrderStatusBar
                  title={t('market-shop:orderStatus.new.title')}
                  message={t('market-shop:orderStatus.new.message')}
                  status="in_progress"
                />
              )}
              {!transactionId && isResidentFlow && (
                <OrderReceive
                  title={t('market-shop:checkOut.receiveOrderTitle')}
                  deliveryType={deliveryType}
                  onDeliveryTypeChange={setDeliveryType}
                />
              )}
              {!transactionId && isResidentFlow && hasProductsInCart && (
                <CheckoutPaymentMethod
                  title={t('market-shop:checkOut.choosePaymentTitle')}
                  value={checkoutPaymentMethod}
                  onChange={handlePaymentMethodChange}
                >
                  {isCardPayment && totalPrice > 0 && (
                    <StripePayment
                      ref={stripePaymentRef}
                      options={{
                        amount: totalPrice,
                        currency: MARKET_SHOP_CURRENCY,
                        mode: 'payment',
                      }}
                    />
                  )}
                </CheckoutPaymentMethod>
              )}
              {!transactionId && isResidentFlow && !!settings?.tipsEnabled && isCardPayment && (
                <Tips
                  prepopulatedTips={settings.tips}
                  prepopulatedTipValue={prepopulatedTipValue}
                  customTipAmount={customTipsAmount}
                  onPrepopulatedTipClick={handlePrepopulatedTipClick}
                  onCustomTipAmountChange={handleCustomTipAmountChange}
                />
              )}
              <OrderSummary
                discount={discount}
                products={Object.values(marketShopStore.cart)}
                transformProductItem={({ product, quantity, note }) => ({
                  label: product.summary,
                  note,
                  price: product.price * quantity,
                  quantity,
                })}
                tipAmount={tipsAmountInCents}
                orderNote={marketShopStore.note}
                subtotal={subTotalPrice}
                tax={taxPrice}
                total={totalPrice}
              />

              {transactionId && isResidentFlow && (
                <OrderReceive
                  title={t('market-shop:checkOut.deliveryMethodTitle')}
                  deliveryType={deliveryType}
                  disabled={true}
                />
              )}

              {transactionId && isResidentFlow && (
                <CheckoutPaymentMethod
                  title={t('market-shop:checkOut.paymentMethodTitle')}
                  value={checkoutPaymentMethod}
                  disabled={true}
                />
              )}
            </S.Modules>
          </S.Content>
          <S.Controls $hasSingleButton={!!transactionId}>
            {transactionId ? (
              <Button
                use="secondary-gray"
                size="large"
                onClick={handleSuccessfulTransactionCloseClick}
              >
                {t('market-shop:checkOut.close')}
              </Button>
            ) : (
              <>
                <Button
                  onlyBox
                  use="secondary-gray"
                  size="large"
                  disabled={processingPayment}
                  onClick={onClose}
                >
                  <Icon icon={CaretLeftIcon} />
                </Button>
                <Button
                  use="primary"
                  size="large"
                  disabled={
                    !hasProductsInCart || (isResidentFlow && checkoutPaymentMethod === null)
                  }
                  loading={isCartFetching || processingPayment}
                  type={isResidentFlow ? 'submit' : 'button'}
                  onClick={handlePayNowClick}
                >
                  {hasToPayNow
                    ? t('market-shop:checkOut.payNow', { amount: formatPrice(totalPrice) })
                    : t('market-shop:checkOut.placeOrder')}
                  {!hasToPayNow && <Icon icon={CheckIcon} />}
                </Button>
              </>
            )}
          </S.Controls>
        </>
      )}
    </PopupWindow>
  )
}
